'use client';

import React from 'react';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import { ImageSlider, ImageWithSignedFallback, Heading } from '@ui';

interface ImageSliderDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const ImageSliderDemo: React.FC<ImageSliderDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const images = [
    {
      key: '01',
      src: 'tomate-cherry-sungold-01',
      alt: t('Sungold Cherry Tomatoes', 'Tomates cherry Sungold'),
    },
    {
      key: '02',
      src: 'hot-pepper-carolina-reaper-02',
      alt: t('Carolina Reaper Hot Pepper', 'Pimiento Carolina Reaper'),
    },
    {
      key: '03',
      src: 'hot-pepper-jays-red-ghost-scorpion-03',
      alt: t("Jay's Red Ghost Scorpion Hot Pepper", 'Pimiento picante Red Ghost Scorpion de Jay'),
    },
    {
      key: '04',
      src: 'tomate-cherry-sungold-01',
      alt: t('Sungold Cherry Tomatoes', 'Tomates cherry Sungold'),
    },
    {
      key: '05',
      src: 'hot-pepper-carolina-reaper-02',
      alt: t('Carolina Reaper Hot Pepper', 'Pimiento Carolina Reaper'),
    },
    {
      key: '06',
      src: 'hot-pepper-jays-red-ghost-scorpion-03',
      alt: t("Jay's Red Ghost Scorpion Hot Pepper", 'Pimiento picante Red Ghost Scorpion de Jay'),
    },
    {
      key: '07',
      src: 'tomate-cherry-sungold-01',
      alt: t('Sungold Cherry Tomatoes', 'Tomates cherry Sungold'),
    },
    {
      key: '08',
      src: 'hot-pepper-carolina-reaper-02',
      alt: t('Carolina Reaper Hot Pepper', 'Pimiento Carolina Reaper'),
    },
    {
      key: '09',
      src: 'hot-pepper-jays-red-ghost-scorpion-03',
      alt: t("Jay's Red Ghost Scorpion Hot Pepper", 'Pimiento picante Red Ghost Scorpion de Jay'),
    },
    {
      key: '10',
      src: 'tomate-cherry-sungold-01',
      alt: t('Sungold Cherry Tomatoes', 'Tomates cherry Sungold'),
    },
    {
      key: '11',
      src: 'hot-pepper-carolina-reaper-02',
      alt: t('Carolina Reaper Hot Pepper', 'Pimiento Carolina Reaper'),
    },
    {
      key: '12',
      src: 'hot-pepper-jays-red-ghost-scorpion-03',
      alt: t("Jay's Red Ghost Scorpion Hot Pepper", 'Pimiento picante Red Ghost Scorpion de Jay'),
    },
  ];

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Image Slider', 'Carrusel de imágenes')}</Heading>
        <p className='sub full'>
          {t('Interactive image slider with:', 'Carrusel interactivo de imágenes con:')}
        </p>
        <ul className='sub full grid-3'>
          <li>
            {t(
              'Large main image area with smooth sliding transitions',
              'Área principal grande con transiciones de deslizamiento suaves'
            )}
          </li>
          <li>{t('Previous/Next arrow buttons', 'Botones de flecha Anterior/Siguiente')}</li>
          <li>{t('Clickable thumbnail navigation', 'Navegacion por miniaturas clickeables')}</li>
          <li>{t('Active image indicator', 'Indicador de imagen activa')}</li>
          <li>
            {t(
              'Keyboard navigation (left/right arrows)',
              'Navegacion con teclado (flechas izquierda/derecha)'
            )}
          </li>
          <li>{t('Touch/drag/swipe support', 'Soporte para toque, arrastrar y deslizar')}</li>
          <li>{t('Responsive layout', 'Diseño responsive')}</li>
          <li>
            {t(
              'Customizable heading tag and class for info panel',
              'Etiqueta y clase de encabezado personalizables para el panel de información'
            )}
          </li>
        </ul>
        <ImageSlider
          headingAs='h4'
          headingClassName='h2'
          images={images}
          renderImage={(img) => (
            <ImageWithSignedFallback
              src={img.src}
              alt={img.alt}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        />
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="image-slider">
  <img src="/images/example.webp" alt="Slide" />
  <button class="btn btn-ghost" type="button">Next</button>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default ImageSliderDemo;
